import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";

export function BrandsMarquee() {
  const { t, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data || []));
  }, []);

  if (brands.length === 0) return null;

  // Split roughly in half for two rows
  const half = Math.ceil(brands.length / 2);
  const row1 = brands.slice(0, half);
  const row2 = brands.slice(half).length > 0 ? brands.slice(half) : brands.slice(0, half);

  const renderLogo = (b, i, prefix) => (
    <Link
      key={`${prefix}-${b.id}-${i}`}
      to={`/brands/${b.slug}`}
      data-testid={`marquee-brand-${prefix}-${i}`}
      className="shrink-0 mx-3 group"
    >
      <div className="w-[180px] h-[90px] bg-white border border-slate-200 hover:border-[#0891b2] rounded-lg flex items-center justify-center px-4 transition-all hover:shadow-md">
        {b.logo || b.image ? (
          <img
            src={b.logo || b.image}
            alt={b.name}
            className="max-h-14 max-w-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
          />
        ) : (
          <span className="font-display text-lg text-slate-500 group-hover:text-[#0891b2] transition-colors">
            {b.name}
          </span>
        )}
      </div>
    </Link>
  );

  return (
    <section className="py-20 bg-slate-50 overflow-hidden" data-testid="brands-marquee">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
            {t.brandsMarquee.eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0891b2] mb-3 leading-tight">
            {t.brandsMarquee.title}
          </h2>
          <p className="text-slate-600">{t.brandsMarquee.subtitle}</p>
        </div>
      </div>

      {/* Row 1 - moves left */}
      <div className="relative">
        <div className="flex animate-marquee-ltr">
          {[...row1, ...row1, ...row1].map((b, i) => renderLogo(b, i, "r1"))}
        </div>
      </div>

      {/* Row 2 - moves right */}
      <div className="relative mt-4">
        <div className="flex animate-marquee-rtl">
          {[...row2, ...row2, ...row2].map((b, i) => renderLogo(b, i, "r2"))}
        </div>
      </div>

      <div className="text-center mt-12">
        <Link
          to="/brands"
          data-testid="marquee-view-all"
          className="inline-flex items-center gap-2 text-[#ef4444] text-sm font-semibold hover:gap-3 transition-all"
        >
          {t.brandsMarquee.viewAll} <Arrow className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
